// packages
import React, { useState } from 'react';
import { DocumentNode, gql } from '@apollo/client';

// components
import { Box, Paper } from '@mui/material';
import { InvoiceViewPresentation } from './InvoiceViewPresentation';
import { InvoiceViewButtonBar } from './InvoiceViewButtonBar';
import { InvoiceViewPaymentInstallments } from './InvoiceViewPaymentInstallments';
import { InvoiceViewTotalsAndAutoPay } from './InvoiceViewTotalsAndAutoPay';
import { InvoiceViewHistory } from './InvoiceViewHistory';
import { InvoiceViewNotesAndLineItemSummary } from './InvoiceViewPresentation/InvoiceViewNotesAndLineItemSummary';
import { InvoiceViewLineItems } from './InvoiceViewLineItems';
import styles from './InvoiceView.module.scss';

// logic
import { InvoiceViewInvoiceFragment } from '#types';
import classNames from 'classnames';
import {
	useContactsCompareForDiff,
	useContactsCompareForDiffContactFragment,
} from '../../hooks/useContactsCompareForDiff';
import { Differences } from '../../lib/helpers/deepCompareAndDiff';

const invoiceViewInvoiceFragment = gql`
	fragment invoiceViewInvoice on Invoice {
		...invoiceViewPresentationInvoice
		...invoiceViewPaymentInstallmentsInvoice
		...invoiceViewButtonBarInvoice
		...invoiceViewNotesAndLineItemSummaryInvoice
		...invoiceViewLineItemsInvoice
		...invoiceViewTotalsAndAutopayInvoice
		...invoiceViewHistoryInvoice
		...usePaymentSectionInvoice
		contact {
			id
			...useContactsCompareForDiffContact
		}
	}
	${ InvoiceViewPresentation.fragments.invoiceViewPresentationInvoiceFragment }
	${ InvoiceViewPaymentInstallments.fragments
		.invoiceViewPaymentInstallmentsInvoiceFragment }
	${ InvoiceViewButtonBar.fragments.invoiceViewButtonBarInvoiceFragment }
	${ InvoiceViewNotesAndLineItemSummary.fragments
		.invoiceViewNotesAndLineItemSummaryInvoiceFragment }
	${ InvoiceViewLineItems.fragments.invoiceViewLineItemsInvoiceFragment }
	${ InvoiceViewTotalsAndAutoPay.fragments
		.invoiceViewTotalsAndAutopayInvoiceFragment }
	${ InvoiceViewHistory.fragments.invoiceViewHistoryInvoiceFragment }
	${ useContactsCompareForDiffContactFragment }
`;

const invoiceViewOrganizationFragment = gql`
	fragment invoiceViewOrganization on Organization {
		...organizationAvatarOrganization
	}
	${ InvoiceViewPresentation.fragments
		.invoiceViewPresentationOrganizationFragment }
`;

export type InvoiceViewProps = {
	tabbedView: boolean;
	invoice?: InvoiceViewInvoiceFragment;
	guestToken?: string;
	requestingUserBaseId?: string;
	requestingUserIsGuest?: boolean;
	fileOrFilename?: File | string | undefined;
	invoiceEdits?: Differences;
};

type InvoiceViewPropsWithFragments = React.FC<InvoiceViewProps> & {
	fragments: {
		invoiceViewInvoiceFragment: DocumentNode;
		invoiceViewOrganizationFragment: DocumentNode;
	};
};

export const InvoiceView: InvoiceViewPropsWithFragments = ( {
	tabbedView,
	invoice,
	guestToken,
	requestingUserBaseId,
	requestingUserIsGuest,
	fileOrFilename,
	invoiceEdits: invoiceEditsProp,
} ) => {
	const [ selectedInstallments, setSelectedInstallments ] = useState<
	{ id: string; dueDate?: string | Date | null }[]
	>( [] );
	const [ shake, setShake ] = useState( false );
	const [ warning, setWarning ] = useState( false );

	// because contactsContext can override local state, we need to consider those contacts as well when considering differences
	const { differences: invoiceEdits } = useContactsCompareForDiff(
		invoice?.contact,
		invoiceEditsProp,
		tabbedView
	);

	return (
		<>
			{ tabbedView ? (
				// When we have tabs we have a clipping container and negative margins to match the tabs
				<Box className={ styles.fakePaperTop }>
					<Paper className={ classNames( styles.container, styles.tabbedView ) }>
						<InvoiceViewPresentation
							invoice={ invoice }
							requestingUserBaseId={ requestingUserBaseId }
						/>
					</Paper>
				</Box>
			) : (
				// when we don't have tabs we have regular `Paper` as the container
				<Paper className={ styles.container }>
					<InvoiceViewPresentation
						invoice={ invoice }
						requestingUserBaseId={ requestingUserBaseId }
					/>
				</Paper>
			) }
			<Paper
				className={ classNames(
					styles.lowerContainer,
					tabbedView ? styles.tabbedView : undefined
				) }
			>
				<InvoiceViewLineItems invoice={ invoice } />
				<InvoiceViewNotesAndLineItemSummary
					invoice={ invoice }
					fileOrFilename={ fileOrFilename }
					guestToken={ guestToken }
				/>
			</Paper>
			<Paper
				className={ classNames(
					styles.lowerContainer,
					tabbedView ? styles.tabbedView : undefined
				) }
			>
				<InvoiceViewPaymentInstallments
					invoice={ invoice }
					guestToken={ guestToken }
					requestingUserBaseId={ requestingUserBaseId }
					warning={ warning }
					shake={ shake }
					selectedInstallments={ selectedInstallments }
					setSelectedInstallments={ setSelectedInstallments }
				/>
				<InvoiceViewTotalsAndAutoPay
					invoice={ invoice }
					requestingUserBaseId={ requestingUserBaseId }
					guestToken={ guestToken }
				/>
			</Paper>
			<InvoiceViewHistory
				invoice={ invoice }
				requestingUserBaseId={ requestingUserBaseId }
			/>
			<InvoiceViewButtonBar
				invoice={ invoice }
				guestToken={ guestToken }
				setWarning={ setWarning }
				setShake={ setShake }
				selectedInstallments={ selectedInstallments }
				setSelectedInstallments={ setSelectedInstallments }
				requestingUserIsGuest={ requestingUserIsGuest }
				requestingUserBaseId={ requestingUserBaseId }
				invoiceEdits={ invoiceEdits }
			/>
		</>
	);
};

InvoiceView.fragments = {
	invoiceViewInvoiceFragment,
	invoiceViewOrganizationFragment,
};
